/**
 * Copyright (c) 2019-Present Syncari - All rights reserved.
 *
 * Design System — FormLayout grid component
 */
import cx from 'classnames';
import React from 'react';

import type { LayoutBreakpoint, LayoutSpacing } from '../../types';

import './FormLayout.less';

export interface FormLayoutProps {
  /**
   * Number of equal-width columns. Default: 1
   */
  columns?: 1 | 2;
  /**
   * Breakpoint at which a 2-column layout collapses to 1 column.
   * Mobile-first: 2 columns appear at this breakpoint and above; 1 column below.
   * Only relevant when columns={2}. Default: 'md'
   */
  collapseAt?: LayoutBreakpoint;
  /** Vertical gap between rows. Default: 4 (16px) */
  rowGap?: LayoutSpacing;
  /** Horizontal gap between columns. Default: 6 (24px) */
  columnGap?: LayoutSpacing;
  className?: string;
  children?: React.ReactNode;
}

const FormLayout: React.FC<FormLayoutProps> = ({
  columns = 1,
  collapseAt = 'md',
  rowGap = 4,
  columnGap = 6,
  className,
  children,
}) => {
  const classes = cx(
    'ds-form-layout',
    columns === 2 && 'ds-form-layout--columns-2',
    columns === 2 && `ds-form-layout--collapse-at-${collapseAt}`,
    `ds-form-layout--row-gap-${rowGap}`,
    `ds-form-layout--col-gap-${columnGap}`,
    className
  );

  return <div className={classes}>{children}</div>;
};

FormLayout.displayName = 'FormLayout';

export { FormLayout };
export default FormLayout;
